import { useState, useEffect } from 'react';
import { toast } from '../toast';
import {
  AccountIcon, LocationIcon, OrderIcon,
  DocumentIcon, FAQIcon, DeleteIcon,
  PhoneIcon, PlusIcon, FacebookIcon, InstagramIcon,
  WhatsAppIcon, CDChatIcon, ChevronRightIcon
} from '../components/Icons';
import { sendChatMessage, uploadInquiryImageToCloudinary, initializePayment, verifyPayment, fetchSettings, verifyOrderPayment } from '../db';

const gridItems = [
  { id: 'edit', label: 'Edit Profile', Icon: AccountIcon },
  { id: 'address', label: 'Delivery Addresses', Icon: LocationIcon },
  { id: 'orders', label: 'My Orders', Icon: OrderIcon },
];

const listItems = [
  { id: 'legal', label: 'Legal & Policies', Icon: DocumentIcon },
  { id: 'faq', label: 'FAQs', Icon: FAQIcon },
  { id: 'delete', label: 'Delete Profile', Icon: DeleteIcon },
];

function MenuItem({ item, onNavigate }) {
  const handleClick = () => {
    if (item.id === 'edit') onNavigate('editprofile');
    if (item.id === 'address') onNavigate('addresses');
    if (item.id === 'orders') onNavigate('orders');
    if (item.id === 'legal') onNavigate('legal');
    if (item.id === 'faq') onNavigate('faq');
    if (item.id === 'delete') onNavigate('deleteprofile');
  };

  return (
    <button
      onClick={handleClick}
      className="w-full flex items-center gap-3 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 px-1 rounded-lg transition-colors"
    >
      <item.Icon className="w-5 h-5 text-[#F47B20] flex-shrink-0" />
      <div className="flex-1 text-left">
        <p className="text-[#1A1A2E] text-sm font-medium">{item.label}</p>
        {item.sub && <p className="text-gray-400 text-xs">{item.sub}</p>}
      </div>
      <ChevronRightIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
    </button>
  );
}

function SupportCard({ onOpenChat }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm">
      <h4 className="font-bold text-[#1A1A2E] text-base mb-1">Contact Manna Bridal Support</h4>
      <p className="text-gray-400 text-sm mb-4">Choose your preferred support channel below.</p>
      <div className="space-y-2 mb-4">
        <button className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 hover:border-[#F47B20] hover:text-[#F47B20] transition-colors text-sm font-medium text-gray-700">
          <PhoneIcon className="w-4 h-4 text-[#F47B20]" /> Toll Free
        </button>
        <button onClick={onOpenChat} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 hover:border-[#F47B20] hover:text-[#F47B20] transition-colors text-sm font-medium text-gray-700">
          <CDChatIcon className="w-4 h-4 text-[#F47B20]" /> MB Chat
        </button>
      </div>

      <div className="flex flex-col items-center mb-4">
        <LocationIcon className="w-5 h-5 text-[#F47B20] mb-1" />
        <p className="text-[#1A1A2E] font-semibold text-sm">Kwara state</p>
        <p className="text-gray-500 text-xs text-center">Manna Bridal Building, Beside Firstbank, Unity Rd, Ilorin, Kwara State, Nigeria.</p>
      </div>

      <div className="border-t border-gray-100 pt-4">
        <p className="text-gray-500 text-sm text-center mb-3">Follow Manna Bridal for updates</p>
        <div className="flex justify-center gap-3">
          {[
            { Icon: FacebookIcon, color: '#1877F2' },
            { Icon: InstagramIcon, color: '#E1306C' },
            { Icon: WhatsAppIcon, color: '#25D366' },
          ].map(({ Icon, color }, i) => (
            <button key={i} className="w-11 h-11 rounded-full border border-gray-200 flex items-center justify-center hover:border-gray-300 transition-colors" style={{ color }}>
              <Icon className="w-5 h-5" />
            </button>
          ))}
        </div>
        <p className="text-gray-300 text-xs text-right mt-3">version 1.0</p>
      </div>
    </div>
  );
}

export default function AccountPage({ onNavigate, onOpenChat, user, onLogout, categories = [], unpaidOrders = [] }) {
  const [showAskProduct, setShowAskProduct] = useState(false);
  const [inquiryName, setInquiryName] = useState('');
  const [inquiryDesc, setInquiryDesc] = useState('');
  const [inquiryCategory, setInquiryCategory] = useState('');
  const [inquiryImage, setInquiryImage] = useState(null);
  const [isSendingInquiry, setIsSendingInquiry] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [paystackKey, setPaystackKey] = useState('');
  const [payingOrderId, setPayingOrderId] = useState(null);

  useEffect(() => {
    fetchSettings().then(s => setPaystackKey(s?.paystack_public_key || '')).catch(() => {});
  }, []);

  const handlePayUnpaid = async (order) => {
    if (!paystackKey) { toast.error('Payment gateway not configured.'); return; }
    setPayingOrderId(order.id);
    try {
      // Step 1: Auto-verify in case they already successfully paid but closed the window previously
      try {
        const verifyRes = await verifyOrderPayment(order.id);
        if (verifyRes.status === 'paid') {
          toast.success(verifyRes.message || 'Payment was already successful!');
          setPayingOrderId(null);
          return; // Stop here, no need to open payment window
        }
      } catch (e) {
        // Ignore verify errors and proceed to let them pay
      }

      // Step 2: Initialize new payment
      const initData = await initializePayment(order.id);
      const handler = window.PaystackPop.setup({
        key: paystackKey,
        email: user?.email,
        amount: initData.amountKobo,
        access_code: initData.accessCode,
        onClose: () => {
          toast.info('Payment window closed. Your order is saved — you can pay later.');
          setPayingOrderId(null);
        },
        callback: (response) => {
          verifyPayment(response.reference)
            .then(() => { toast.success('Payment successful!'); setPayingOrderId(null); })
            .catch(() => { toast.error('Payment verification failed.'); setPayingOrderId(null); });
        },
      });
      handler.openIframe();
    } catch (e) {
      toast.error('Could not initialize payment.');
      setPayingOrderId(null);
    }
  };

  const handleLogoutClick = async () => {
    setIsLoggingOut(true);
    try {
      await onLogout();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Derive display info from the logged-in user
  const displayName = user ? user.name : null;
  const displayEmail = user ? user.email : null;
  const initials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : null;

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryName || !inquiryDesc) {
      toast.error('Please fill out all required fields.');
      return;
    }
    setIsSendingInquiry(true);
    let imageUrl = '';
    if (inquiryImage) {
      try {
        imageUrl = await uploadInquiryImageToCloudinary(inquiryImage);
      } catch (err) {
        toast.error('Failed to upload image.');
        setIsSendingInquiry(false);
        return;
      }
    }

    let msg = `Product Inquiry:\nProduct: ${inquiryName}\nCategory: ${inquiryCategory || 'Any'}\nDetails: ${inquiryDesc}`;
    if (imageUrl) {
      msg += `\nAttached Image: ${imageUrl}`;
    }
    
    try {
      await sendChatMessage(msg);
      setInquiryName('');
      setInquiryDesc('');
      setInquiryCategory('');
      setInquiryImage(null);
      setShowAskProduct(false);
      onOpenChat(); // Automatically open chat for them
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSendingInquiry(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF3F0]">

      {/* ── Unified layout ── */}
      <div className="px-4 lg:px-8 py-4 lg:py-6 mb-24 lg:mb-0 max-w-5xl mx-auto">
        <h1 className="text-xl font-black text-[#1A1A2E] mb-4">Profile</h1>

        {/* Profile header card */}
        <div className="bg-[#F47B20] rounded-2xl p-4 mb-5 flex flex-col sm:flex-row items-center sm:justify-between gap-4 text-center sm:text-left shadow-sm">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center border-2 border-white/30 shrink-0">
              {user ? (
                <span className="text-white font-black text-lg">{initials}</span>
              ) : (
                <AccountIcon className="w-7 h-7 text-white" />
              )}
            </div>
            <div>
              {user ? (
                <>
                  <p className="text-white font-bold text-base">{displayName}</p>
                  <p className="text-white/80 text-xs break-all">{displayEmail}</p>
                </>
              ) : (
                <>
                  <p className="text-white font-bold text-base">Please Sign In</p>
                  <p className="text-white/80 text-xs">Sign in to access your full profile</p>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap justify-center sm:justify-end gap-2 w-full sm:w-auto">
            {user ? (
              <>
                {user.role === 'admin' && (
                  <button onClick={() => onNavigate('admin')} className="bg-white text-[#F47B20] font-bold px-4 py-2 rounded-xl text-xs shadow-sm hover:bg-gray-50 transition-colors w-full sm:w-auto">
                    Admin Dashboard
                  </button>
                )}
                <button onClick={handleLogoutClick} disabled={isLoggingOut} className="bg-white/20 text-white font-bold px-4 py-2 rounded-xl text-xs hover:bg-white/30 transition-colors border border-white/30 w-full sm:w-auto disabled:opacity-50">
                  {isLoggingOut ? 'Logging Out...' : 'Log Out'}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => onNavigate('login')} className="bg-white text-[#F47B20] font-bold px-4 py-2 rounded-xl text-xs hover:bg-orange-50 transition-colors w-full sm:w-auto">
                  Sign In
                </button>
                <button onClick={() => onNavigate('signup')} className="bg-white/20 text-white font-bold px-4 py-2 rounded-xl text-xs hover:bg-white/30 transition-colors border border-white/30 w-full sm:w-auto">
                  Create Account
                </button>
              </>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="flex gap-3 mb-5">
          <button onClick={() => setShowAskProduct(true)} className="bg-white rounded-xl px-4 py-2 flex items-center gap-2 text-[#1A1A2E] text-xs font-bold border border-gray-100 hover:border-[#F47B20] hover:text-[#F47B20] shadow-sm transition-colors">
            <PlusIcon className="w-4 h-4 text-[#F47B20]" /> Ask about a product
          </button>
        </div>

        {/* Unpaid Orders Section */}
        {user && unpaidOrders.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Pending Payment</span>
              <span className="bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">{unpaidOrders.length}</span>
            </div>
            <div className="space-y-2">
              {unpaidOrders.map(order => (
                <div key={order.id} className="bg-white rounded-2xl p-4 border border-orange-100 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#1A1A2E] truncate">#{order.reference || order.id}</p>
                      <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                      <p className="text-sm font-black text-[#F47B20] mt-0.5">₦{Number(order.total).toLocaleString('en-NG')}</p>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button
                        onClick={() => handlePayUnpaid(order)}
                        disabled={payingOrderId === order.id}
                        className="bg-[#F47B20] hover:bg-[#D96A10] disabled:bg-orange-300 text-white font-bold px-4 py-2 rounded-xl text-xs transition-colors shadow-sm"
                      >
                        {payingOrderId === order.id ? 'Processing...' : 'Pay Now'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
          {/* Settings */}
          <div>
            {user && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                {gridItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.id === 'edit') onNavigate('editprofile');
                      if (item.id === 'address') onNavigate('addresses');
                      if (item.id === 'orders') onNavigate('orders');
                    }}
                    className="bg-white rounded-xl px-2 py-3 flex flex-col items-center justify-center gap-2 border border-gray-100 hover:border-[#F47B20]/30 hover:shadow-sm transition-all text-center"
                  >
                    <div className="w-10 h-10 bg-[#FEF0E6] rounded-full flex items-center justify-center flex-shrink-0">
                      <item.Icon className="w-5 h-5 text-[#F47B20]" />
                    </div>
                    <p className="font-semibold text-[#1A1A2E] text-xs leading-tight break-words w-full">{item.label}</p>
                  </button>
                ))}
              </div>
            )}

            <div className="bg-white rounded-2xl p-4 shadow-sm">
              {listItems.map(item => (
                <MenuItem key={item.id} item={item} onNavigate={onNavigate} />
              ))}
              {/* Duplicate Sign In / Log Out list items removed as they exist in header */}
            </div>
          </div>

          <SupportCard onOpenChat={onOpenChat} />
        </div>
      </div>

      {/* ── Ask about a Product Modal ── */}
      {showAskProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl border border-gray-100 flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="font-black text-lg text-[#1A1A2E]">Ask about a Product</h3>
                <p className="text-xs text-gray-500">Looking for a specific bridal item? Request it here!</p>
              </div>
              <button onClick={() => setShowAskProduct(false)} className="text-gray-400 hover:text-gray-700 text-lg font-bold">✕</button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form onSubmit={handleInquirySubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Product Name *</label>
                  <input
                    type="text" required value={inquiryName} onChange={e => setInquiryName(e.target.value)}
                    placeholder="e.g. Swarovski Crystal Gown"
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#F47B20] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Category</label>
                  <select value={inquiryCategory} onChange={e => setInquiryCategory(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:border-[#F47B20]">
                    <option value="">Select a category</option>
                    {categories.map(c => (
                      <option key={c.id || c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Description & details *</label>
                  <textarea required rows={3} value={inquiryDesc} onChange={e => setInquiryDesc(e.target.value)}
                    placeholder="Provide details about the style, size requirements, or colors..."
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#F47B20] outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Attach Image (Optional)</label>
                  <input
                    type="file" accept="image/*"
                    onChange={e => setInquiryImage(e.target.files[0])}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-[#FEF0E6] file:text-[#F47B20] hover:file:bg-[#FCE3D1] transition-colors"
                  />
                </div>
                <button type="submit" disabled={isSendingInquiry} className="w-full py-3.5 bg-[#F47B20] hover:bg-[#D96A10] disabled:bg-orange-300 text-white font-bold rounded-xl text-sm transition-colors shadow-md flex items-center justify-center gap-2">
                  {isSendingInquiry ? 'Sending to Chat...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
